const _ = require('lodash')
const browser = require('bowser')
const cookie = require('js-cookie')
const uid = require('node-uuid')
const verge = require('verge')
const { send: ajax } = require('client/lib/request')
const { localStorage } = require('client/lib/storage')
const Promise = require('bluebird')

// Configuration for client v1-v2 cross-compatibility
function NOT_CONFIGURED() {
 throw new Error('analytics lib is not configured!')
}
let userId = null
let isBot = null
let isLoggedIn = null
let experiments = {}
let buildNumber = null
let getPageName = NOT_CONFIGURED
let getSessionId = NOT_CONFIGURED

// Attempt to auto-configure for v1 (wiggin) client
if (typeof wiggin !== 'undefined') {
  configure({
    user: wiggin.user,
    buildNumber: wiggin.buildNumber,
    getPageName: pageContext => _.get(pageContext, 'page_name', app.state('page')),
    getSessionId: () => app.sess.update()
  })
}

// Some browsers omit empty objects; __keep ensures the pageContext is always sent.
const EMPTY_PAGE_CONTEXT = { '__keep': true }
const STREAM_FIELD_KEYS = ['stream_type', 'stream_id', 'stream_key']
const DEFAULT_FIELDS = {
  context: {
    'app_name': browser.name,
    'app_version': browser.version
  }
}

let currentPageContext = null
let referrerPageContext = null
let branchIdentity = null

module.exports = {
  configure,
  segmentIdentify,
  branchIdentify,
  getPageContext,
  setPageContext,
  revertPageContext,
  withTemporaryPageContext,
  persistPageContext,
  getPersistedPageContext,
  normalizePageContext,

  track: function (eventName, fields = {}, optCallback = _.noop) {
    if (isBot) {
      optCallback()
      return Promise.resolve()
    }

    const mbProperties = fields.metabase || {}
    delete fields.metabase

    // Overlay the fields context on top of the page context
    const fieldsContext = normalizePageContext(fields['page_context'])
    delete fields['page_context']
    const pageContext = _.merge({}, getPageContext(), fieldsContext)
    const pageName = getPageName(pageContext)

    if (!fields.noThirdPartyTracking) {
      const branchParams = _.get(window, 'branchInitData.data_parsed', {})
      branchParams['branch_identity_id'] = (
        _.get(branchIdentity, 'identity_id') ||
        _.get(window, 'branchInitData.identity')
      )
      const testGroups = Object.entries(experiments)
      .map(pair => pair.join('='))
      .join(',')

      // straight pass-thru to Segment
      const extraProperties = _.merge({
        page: pageName,
        'test_groups': testGroups,
        'branch_params': branchParams
      }, fields)
      window.analytics.track(eventName, extraProperties)
    }

    // Do not track internal events for logged out users.
    if (!isLoggedIn) {
      optCallback()
      return Promise.resolve()
    }

    let utmProperties = {}
    if (fields.utmParams) {
      utmProperties = { 'client_session': { 'extra_params': { utm: fields.utmParams } } }
    }

    const vp = verge.viewport()
    const data = _.merge({
      action: 'track',
      event: sanitizeEventName(eventName),
      'session_id': fields.sessionId || getSessionId(),
      'request_id': uid.v4(),
      'user_id': userId,
      'build_number': buildNumber,
      'page_context': pageContext,
      context: {
        'screen_height': vp.height,
        'screen_width': vp.width
      },
      properties: _.merge({
        'page_name': pageName,
        'resulting_action_type': eventName
      }, mbProperties, utmProperties)
    }, DEFAULT_FIELDS)

    return ajax({
      url: '/api/v0.1/track',
      method: 'post',
      type: 'json',
      data
    })
    .finally(optCallback)
  }
}

function configure(options) {
  if (options.user) {
    userId = options.user.id
    isBot = options.user.bot
    isLoggedIn = options.user.loggedIn
    experiments = options.user.experiments
  }
  if (options.buildNumber) {
    buildNumber = options.buildNumber
  }
  if (options.getPageName) {
    getPageName = options.getPageName
  }
  if (options.getSessionId) {
    getSessionId = options.getSessionId
  }
}

function normalizePageContext(obj) {
  return _.transform(obj, (acc, value, key) => {
    const normalizedKey = _.snakeCase(key)
    if (value !== undefined) {
      acc[normalizedKey] = value
    }
  })
}

function getPageContext() {
  const pageContext = _.clone(currentPageContext || EMPTY_PAGE_CONTEXT)
  if (referrerPageContext) {
    _.each(referrerPageContext, (value, key) => {
      pageContext[`referrer_${key}`] = value
    })
  }
  return pageContext
}

function setPageContext(newPageContext, persistStreamContext) {
  let normalizedPageContext = normalizePageContext(newPageContext)
  if (!referrerPageContext) {
    referrerPageContext = getPersistedPageContext()
  }
  if (currentPageContext) {
    referrerPageContext = _.clone(currentPageContext)
    // Typically, when we jump from a stream (ex: mix profile) to card view,
    // the stream_id and stream_type should stay unchanged, but the page_name should change.
    if (persistStreamContext) {
      const currentStream = _.pick(currentPageContext, STREAM_FIELD_KEYS)
      normalizedPageContext = _.merge(currentStream, normalizedPageContext)
    }
  }
  currentPageContext = normalizedPageContext
}

function revertPageContext(pageContextWithReferrer) {
  // This is intended to use the result from getPageContext (which includes the referrer)
  const prefix = 'referrer_'
  referrerPageContext = _.transform(pageContextWithReferrer, (acc, value, key) => {
    if (key.startsWith(prefix)) {
      acc[key.slice(prefix.length)] = value
    }
  })
  currentPageContext = _.transform(pageContextWithReferrer, (acc, value, key) => {
    if (!key.startsWith(prefix)) {
      acc[key] = value
    }
  })
}

function withTemporaryPageContext(temporaryPageContext, callback) {
  if (!temporaryPageContext) {
    callback()
    return
  }
  const currentPageContext = getPageContext()
  setPageContext(temporaryPageContext)
  callback()
  revertPageContext(currentPageContext)
}

const PERSISTENCE_KEY = 'pageContextPersistence'
const PERSISTENCE_TTL = 30000

function persistPageContext(overlayContext = {}) {
  localStorage.removeItem(PERSISTENCE_KEY)
  if (currentPageContext) {
    const normalizedOverlayContext = normalizePageContext(overlayContext)
    const persistenceValue = JSON.stringify({
      pageContext: _.merge({}, currentPageContext, normalizedOverlayContext),
      ttl: Date.now() + PERSISTENCE_TTL
    })
    localStorage.setItem(PERSISTENCE_KEY, persistenceValue)
  }
}

function getPersistedPageContext() {
  const persistenceValue = localStorage.getItem(PERSISTENCE_KEY)
  localStorage.removeItem(PERSISTENCE_KEY) // Burn after reading
  if (persistenceValue) {
    const persistenceObj = JSON.parse(persistenceValue)
    if (persistenceObj.ttl > Date.now()) {
      return persistenceObj.pageContext
    }
  }
}

function sanitizeEventName(event) {
  switch (event) {
    case 'search-topic':
      return 'search'

    case 'pageview':
      return 'page_load'

    case 'paginate':
      return 'interaction_swipe'

    default:
      return 'interaction_tap'
  }
}

function segmentIdentify(user) {
  if (!user.loggedIn) {
    return
  }

  // Version the user identified cookie since sometimes we need to ensure
  // that updated data happens on deploys of new code
  const USER_IDENTIFIED_VERSION = '5'

  // https://segment.com/docs/spec/identify/
  if (cookie.get('user_identified') != USER_IDENTIFIED_VERSION) {
    // Convert providers Object to comma-separated keys string
    const providers = Object.keys(user.providers).join(',')
    // Convert experiments Object into a comma-separated key=value string
    const testGroups = Object.entries(user.experiments)
    .map(pair => pair.join('='))
    .join(',')

    window.analytics.identify(user.id, {
      'access_granted_at': user['access_granted_at'],
      'birthday': user.birthday,
      'created_at': user['created_at'],
      'display_name': user['display_name'],
      'email': user.email,
      'gender': user.gender,
      'name': user['display_name'],
      'providers': providers,
      'signup_context': user['signup_context'],
      'test_groups': testGroups,
      'username': user.username,
      'user_type': user['user_type'],
      'category': user.category
    })
    cookie.set('user_identified', USER_IDENTIFIED_VERSION)
  }
}

function branchIdentify(user) {
  // Since we don't load the branch sdk on every page, but
  // use a consistent place for identifying users
  // we need to check that the branch script has been run by
  // checking if the branch object is defined on the window
  if (!user.loggedIn || !window.branch) {
    return
  }
  window.branch.setIdentity(_.toString(user.id), (err, data) => {
    branchIdentity = data
  })
}
